import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getQueryKey as getTopicQueryKey } from "@app/analytics/TopicReport/hooks/useTopicReport";
import useTopicReportImpersonations from "@app/analytics/TopicReport/hooks/useTopicReportImpersonations";
import * as Events from "@app/analytics/tracking/events";
import type {
  Topic,
  TopicImpersonationDeleteRequest,
} from "@app/analytics/types";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import type { PartialBy } from "@app/common/types";

import { getQueryKey as getTopicListQueryKey } from "./useTopicReports";

type RemoveTopicReportRequest = PartialBy<
  TopicImpersonationDeleteRequest,
  "entityId" | "entityKind"
>;

const useRemoveTopicReport = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { deleteImpersonation } = useTopicReportImpersonations({
    enabled: false,
  });

  const refetchTopics = (topicId: Topic["id"]) => {
    queryClient.invalidateQueries({
      queryKey: getTopicQueryKey({ topicId, userId: user?.id }),
    });
    queryClient.invalidateQueries({ queryKey: getTopicListQueryKey(user?.id) });
  };

  const removeTopicReportMutation = useMutation({
    mutationFn: (id: Topic["id"]) =>
      apiClient.topics.patchTopic(id, { isHidden: true }),
    onSuccess: () => {
      Events.deleteReport({ reportType: "readout" });
    },
    onSettled: (data, error, topicId) => refetchTopics(topicId),
  });

  const removeTopicReport = (
    request: RemoveTopicReportRequest,
    opts?: {
      onSuccess?: () => void;
      onError?: () => void;
      onSettled?: () => void;
    }
  ) => {
    const isSharedReport = request.ownerUserId != user?.id;
    if (isSharedReport) {
      return deleteImpersonation.mutate(
        { entityId: `${user?.id}`, entityKind: "user", ...request },
        opts
      );
    }
    return removeTopicReportMutation.mutate(request.topicId, opts);
  };

  return {
    removeTopicReport,
    isRemoving:
      deleteImpersonation.isPending || removeTopicReportMutation.isPending,
  };
};

export default useRemoveTopicReport;
